import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

import { toEntityLinkVerdict } from "~/ai-chat/data-source/use-entity-link-verdict";
import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import {
  collectConversationArtifacts,
  type ConversationArtifact,
  type VerifiedConversationArtifact,
} from "~/ai-chat/model/conversation-artifacts";
import { resourceExistsQueryOptions } from "~/resource/data-source/api/resource-exists-query-options";

// The Artifacts panel is derived from the assistant's links, which can name resources that
// don't exist. Confirmed misses are dropped; pending and unverifiable ones stay listed with
// their verdict so a slow or failed check never empties the panel. The lookups share cache
// entries with the transcript chips. Resources saved from this chat were just created, so
// they skip the lookup — the list endpoint can lag the write and would misreport them.
export function useConversationArtifacts(
  messages: ChatTranscriptItem[],
  savedArtifacts: ConversationArtifact[],
): VerifiedConversationArtifact[] {
  // Stable references for useQueries (ref-sensitive: it subscribes per array entry).
  const artifacts = useMemo(() => collectConversationArtifacts(messages, savedArtifacts), [messages, savedArtifacts]);
  const savedIds = useMemo(() => new Set(savedArtifacts.map((artifact) => artifact.resourceId)), [savedArtifacts]);
  return useQueries({
    queries: artifacts.map((artifact) => ({
      ...resourceExistsQueryOptions(artifact.resourceId),
      enabled: !savedIds.has(artifact.resourceId),
    })),
    combine: (results): VerifiedConversationArtifact[] =>
      artifacts.flatMap((artifact, index) => {
        const verdict = savedIds.has(artifact.resourceId) ? "found" : toEntityLinkVerdict(results[index], false);
        return verdict === "missing" ? [] : [{ ...artifact, verdict }];
      }),
  });
}
